import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { Badge, type BadgeVariant } from '~/components/ui/Badge';
import { PageTitle } from '~/components/ui/Page';
import { Table } from '~/components/ui/Table';
import { useStudentFocus } from '~/modules/studentFocus';
import buttonStyles from '~/styles/button.module.css';

type QuizResponseSummary = components['schemas']['QuizResponseSummary'];

type QuizSubmissionsSearch = {
  user_id?: number;
};

export const Route = createFileRoute('/_admin/quiz-submissions/')({
  component: QuizSubmissionsList,
  validateSearch: (search: Record<string, unknown>): QuizSubmissionsSearch => {
    const raw = search.user_id;
    const id =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw !== ''
          ? Number(raw)
          : undefined;
    return {
      user_id: id !== undefined && Number.isFinite(id) ? id : undefined,
    };
  },
});

const columnHelper = createColumnHelper<QuizResponseSummary>();

function statusBadge(row: QuizResponseSummary): {
  label: string;
  variant: BadgeVariant;
} {
  if (!row.complete) return { label: 'In progress', variant: 'neutral' };
  if (row.passed) return { label: 'Passed', variant: 'success' };
  return { label: 'Failed', variant: 'error' };
}

const columns = [
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('user_display_name', {
    header: 'Student',
    cell: (info) => (
      <div>
        <div>{info.getValue()}</div>
        <small style={{ color: 'var(--grey-600)' }}>
          {info.row.original.user_email}
        </small>
      </div>
    ),
  }),
  columnHelper.accessor('course_name', { header: 'Course' }),
  columnHelper.accessor('lesson_name', { header: 'Lesson' }),
  columnHelper.accessor('score', {
    header: 'Score',
    cell: (info) => `${info.getValue()}%`,
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: (info) => {
      const { label, variant } = statusBadge(info.row.original);
      return <Badge $variant={variant}>{label}</Badge>;
    },
  }),
  columnHelper.accessor('created_at', {
    header: 'Submitted',
    cell: (info) => new Date(info.getValue()).toLocaleString(),
  }),
];

function QuizSubmissionsList() {
  const search = Route.useSearch();
  const [page, setPage] = useState(0);
  const { focusedStudent } = useStudentFocus();

  const effectiveUserId = search.user_id ?? focusedStudent?.id;

  const { data, isLoading, error } = api.listQuizResponses.useQuery({
    query: {
      limit: 10,
      offset: 10 * page,
      ...(effectiveUserId !== undefined ? { user_id: effectiveUserId } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <PageTitle>Quiz Submissions</PageTitle>

      {search.user_id !== undefined ? (
        <FilterBanner>
          <span>
            Filtered to submissions by student{' '}
            <Link
              to="/students/$studentId"
              params={{ studentId: String(search.user_id) }}
            >
              #{search.user_id}
            </Link>
          </span>
          <Link to="/quiz-submissions" search={{}}>
            Clear filter
          </Link>
        </FilterBanner>
      ) : null}

      {isLoading ? (
        <LoadingMessage>Loading Quiz Submissions...</LoadingMessage>
      ) : error ? (
        <ErrorMessage>Error loading Quiz Submissions</ErrorMessage>
      ) : (
        <>
          <Toolbar>
            <div style={{ flexGrow: 1 }}>Page {page + 1}</div>
            <Button
              disabled={page === 0}
              className={buttonStyles.Button}
              onClick={() => setPage((v) => v - 1)}
            >
              Previous
            </Button>
            <Button
              className={buttonStyles.Button}
              onClick={() => setPage((v) => v + 1)}
            >
              Next
            </Button>
          </Toolbar>

          <Table
            table={table}
            to="/quiz-submissions/$submissionId"
            getParams={(row) => ({ submissionId: String(row.id) })}
          />
        </>
      )}
    </div>
  );
}

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  margin: var(--component-margin);
  gap: var(--component-margin);
`;

const FilterBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.625rem 0.875rem;
  margin: var(--component-margin);
  background: color-mix(in srgb, var(--blue) 8%, transparent);
  border-left: 3px solid var(--blue);
  border-radius: 4px;

  a {
    color: var(--blue);
  }
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
